import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function LandingPage() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const target = 2847;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      setCount(c => {
        if (c + step >= target) { clearInterval(timer); return target; }
        return c + step;
      });
    }, 30);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: '⚡',
      title: 'Bulk Email Sending',
      desc: 'Blast thousands of emails in hours, not days. Load your list, write your message, hit send — Bulkify handles the rest automatically.',
    },
    {
      icon: '🔄',
      title: 'Auto Account Switching',
      desc: 'Connect up to 50 Gmail accounts. Bulkify rotates between them automatically so you never hit a sending limit again.',
    },
    {
      icon: '🎯',
      title: 'Smart Follow-ups',
      desc: 'Set up multi-step sequences. If they don\'t reply, Bulkify follows up automatically. Stop when they reply. Close more deals on autopilot.',
    },
    {
      icon: '📊',
      title: 'Real-Time Analytics',
      desc: 'Track every open, click, reply, bounce and unsubscribe in real time. Know exactly who\'s engaging and who\'s not.',
    },
    {
      icon: '🛡️',
      title: 'Email Integrity Checker',
      desc: 'Before you send, Bulkify checks every Gmail account for token health, daily limits, bounce rates and spam reputation.',
    },
    {
      icon: '🔥',
      title: 'Email Warmer',
      desc: 'New accounts need warming before blasting. Bulkify automatically warms your accounts by sending emails between them — building trust with Gmail.',
    },
    {
      icon: '👤',
      title: 'Personalization',
      desc: 'Insert {{first_name}}, {{company}}, {{custom_field}} in every email. Every recipient gets a message that feels written just for them.',
    },
    {
      icon: '📁',
      title: 'CSV & TXT Import',
      desc: 'Import thousands of leads from CSV or plain TXT files. Names are auto-extracted from email addresses. No formatting headaches.',
    },
    {
      icon: '🕐',
      title: 'Campaign ETA',
      desc: 'See exactly when your campaign will finish — a live countdown timer tells you how long is left and the exact estimated finish time.',
    },
  ];

  const steps = [
    { num: '01', title: 'Link your Gmails', desc: 'Connect as many Gmail accounts as you want in seconds using Google OAuth.' },
    { num: '02', title: 'Import your leads', desc: 'Upload a CSV or TXT file with your contacts. Done in seconds.' },
    { num: '03', title: 'Write your sequence', desc: 'Write your email + follow-ups. Add personalization variables.' },
    { num: '04', title: 'Launch & watch', desc: 'Hit launch. Watch emails go out in real time. Replies stop the sequence automatically.' },
  ];

  const testimonials = [
    { name: 'Adaeze O.', role: 'Sales Manager, Lagos', text: 'I sent 3,000 emails in one day with 4 Gmail accounts. Got 47 replies. This thing is insane.', avatar: 'A' },
    { name: 'Chukwudi M.', role: 'Founder, Abuja', text: 'The follow-up feature alone is worth everything. I close 3x more deals now because no lead slips through.', avatar: 'C' },
    { name: 'Fatima B.', role: 'Digital Marketer', text: 'I tried Mailmeteor, Lemlist, everything. Bulkify does more and it\'s free. I\'m telling everyone.', avatar: 'F' },
  ];

  return (
    <div style={{ background: '#0a0b0d', color: '#f0f1f3', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', overflowX: 'hidden' }}>

      {/* NAV */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 40px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(10,11,13,0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 150, height: 60, borderRadius: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: '0 0 20px rgba(255,255,255,0.4)', animation: 'logoPulse 2.5s ease-in-out infinite' }}>
            <img src="/logo.png" alt="Bulkify" style={{ width: 148, height: 60, objectFit: 'contain' }} onError={e => e.target.parentElement.style.background='#3b82f6'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={() => navigate('/login')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#f0f1f3', padding: '8px 20px', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
            Sign in
          </button>
          <button onClick={() => navigate('/login')} style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', border: 'none', color: '#fff', padding: '8px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Get started free →
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '120px 24px 80px', position: 'relative', overflow: 'hidden' }}>
        {/* Background glow */}
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 800, height: 400, background: 'radial-gradient(ellipse, rgba(99,102,241,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />

        {/* Badge */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 100, padding: '6px 16px', marginBottom: 32, fontSize: 13, color: '#a5b4fc' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block', animation: 'pulse 2s infinite' }} />
          🎉 100% FREE during launch — no credit card required
        </div>

        <h1 style={{ fontSize: 'clamp(40px, 7vw, 80px)', fontWeight: 800, letterSpacing: '-2px', lineHeight: 1.1, marginBottom: 24, maxWidth: 900 }}>
          Send{' '}
          <span style={{ background: 'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Thousands of Emails
          </span>
          {' '}While You Sleep
        </h1>

        <p style={{ fontSize: 'clamp(16px, 2.5vw, 22px)', color: '#8b909a', maxWidth: 640, lineHeight: 1.6, marginBottom: 48 }}>
          Bulkify is the most powerful free email outreach platform ever built. Multi-account sending, auto follow-ups, real-time tracking, email warming — everything you need to close deals at scale.
        </p>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 64 }}>
          <button onClick={() => navigate('/login')} style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', border: 'none', color: '#fff', padding: '16px 36px', borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: 'pointer', boxShadow: '0 0 40px rgba(99,102,241,0.4)' }}>
            Start for free — no card needed →
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { num: `${count.toLocaleString()}+`, label: 'Emails sent today' },
            { num: '50+', label: 'Gmail accounts supported' },
            { num: '100%', label: 'Free right now' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, background: 'linear-gradient(135deg, #3b82f6, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{s.num}</div>
              <div style={{ fontSize: 13, color: '#555b66', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FREE BANNER */}
      <div style={{ background: 'linear-gradient(135deg, #1d3a6b, #2d1b69)', borderTop: '1px solid rgba(99,102,241,0.3)', borderBottom: '1px solid rgba(99,102,241,0.3)', padding: '20px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 16, color: '#a5b4fc', margin: 0 }}>
          🚀 <strong style={{ color: '#fff' }}>Limited time offer:</strong> Every single feature is completely FREE. No subscriptions. No limits. No catch.
          {' '}<strong style={{ color: '#fbbf24' }}>This won't last forever — get in now.</strong>
        </p>
      </div>

      {/* FEATURES */}
      <section style={{ padding: '100px 24px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Everything you need</div>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 16 }}>
            Tools that make competitors{' '}
            <span style={{ background: 'linear-gradient(135deg, #ec4899, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>charge $99/month</span>
          </h2>
          <p style={{ color: '#555b66', fontSize: 18 }}>All of this. Completely free. Right now.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '28px 28px', position: 'relative', overflow: 'hidden', transition: 'border-color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
            >
              <div style={{ fontSize: 36, marginBottom: 16 }}>{f.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, letterSpacing: '-0.3px' }}>{f.title}</h3>
              <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ padding: '80px 24px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Dead simple</div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-1px' }}>Up and running in 4 steps</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(168,85,247,0.2))', border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 18, fontWeight: 800, color: '#a5b4fc' }}>
                  {s.num}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section style={{ padding: '100px 24px', maxWidth: 800, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: 16 }}>
            Why pay when Bulkify is{' '}
            <span style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>free?</span>
          </h2>
        </div>
        <div style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', background: 'rgba(255,255,255,0.04)', padding: '16px 24px', gap: 16 }}>
            <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Feature</div>
            {['Bulkify', 'Lemlist', 'Mailmeteor'].map(n => (
              <div key={n} style={{ fontSize: 13, fontWeight: 700, textAlign: 'center', color: n === 'Bulkify' ? '#a5b4fc' : '#6b7280' }}>{n}</div>
            ))}
          </div>
          {[
            ['Multi-account sending', '✅', '❌', '❌'],
            ['Auto follow-ups', '✅', '✅', '❌'],
            ['Email warmer', '✅', '✅', '❌'],
            ['Integrity checker', '✅', '❌', '❌'],
            ['Real-time tracking', '✅', '✅', '✅'],
            ['Pause & resume', '✅', '✅', '❌'],
            ['Price', '🆓 FREE', '$99/mo', '$49/mo'],
          ].map(([feat, ...vals], i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '14px 24px', gap: 16, borderTop: '1px solid rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
              <div style={{ fontSize: 14, color: '#d1d5db' }}>{feat}</div>
              {vals.map((v, j) => (
                <div key={j} style={{ textAlign: 'center', fontSize: 14, fontWeight: j === 0 ? 700 : 400 }}>{v}</div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ padding: '80px 24px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-1px' }}>People are going crazy for it</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {testimonials.map((t, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: 28 }}>
                <div style={{ color: '#fbbf24', fontSize: 16, marginBottom: 16 }}>★★★★★</div>
                <p style={{ color: '#d1d5db', fontSize: 15, lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic' }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>{t.avatar}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '100px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 300, background: 'radial-gradient(ellipse, rgba(99,102,241,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'inline-block', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 100, padding: '6px 18px', fontSize: 13, color: '#fbbf24', marginBottom: 24 }}>
            ⏰ Free access won't last forever
          </div>
          <h2 style={{ fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 20 }}>
            Stop paying for email tools.<br />
            <span style={{ background: 'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Bulkify is free right now.
            </span>
          </h2>
          <p style={{ color: '#6b7280', fontSize: 18, marginBottom: 40, maxWidth: 500, margin: '0 auto 40px' }}>
            Join thousands of users sending at scale for free. No credit card. No catch. Just results.
          </p>
          <button onClick={() => navigate('/login')} style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', border: 'none', color: '#fff', padding: '18px 48px', borderRadius: 14, fontSize: 18, fontWeight: 800, cursor: 'pointer', boxShadow: '0 0 60px rgba(99,102,241,0.5)', letterSpacing: '-0.3px' }}>
            Create your free account →
          </button>
          <p style={{ color: '#374151', fontSize: 13, marginTop: 16 }}>No credit card · No setup fees · Cancel anytime</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '32px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ width: 150, height: 60, borderRadius: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: '0 0 20px rgba(255,255,255,0.4)', animation: 'logoPulse 2.5s ease-in-out infinite' }}>
          <img src="/logo.png" alt="Bulkify" style={{ width: 148, height: 60, objectFit: 'contain' }} onError={e => e.target.parentElement.style.background='#3b82f6'} />
        </div>
        </div>
        <p style={{ color: '#374151', fontSize: 13 }}>
          Built by{' '}
          <a href="https://x.com/meshcode2" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>meshcode</a>
          {' '}· Smart email outreach for everyone
        </p>
      </footer>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes logoPulse { 0%, 100% { box-shadow: 0 0 20px rgba(255,255,255,0.4); } 50% { box-shadow: 0 0 40px rgba(255,255,255,0.8), 0 0 60px rgba(255,255,255,0.3); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0a0b0d; }
        button { font-family: inherit; }
      `}</style>
    </div>
  );
}
